import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/auth'
import { invalidateLibraryQueries } from '@/hooks/books/useBooks'
import type { LibraryBook, LibraryGenre } from '@/lib/insights'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import { getTranslatedGenreName, type TranslatableGenre } from '@/lib/utils'

type GenreRow = TranslatableGenre & { id: string; color: string }

/**
 * Every book of the user with localized genres, flattened for client-side insights.
 * The key starts with ['books', userId] so book mutations invalidate it.
 */
export function useLibraryBooks() {
  const { user } = useAuth()
  const { i18n } = useTranslation()
  const language = i18n.language

  return useQuery({
    queryKey: ['books', user?.id, 'library', language],
    queryFn: async (): Promise<LibraryBook[]> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('books')
        .select(`
          *,
          book_genres (
            genre:genres (
              id,
              name,
              name_pt,
              name_en,
              name_es,
              color
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        captureError(error, { context: 'useLibraryBooks.queryFn', userId: user.id })
        throw error
      }

      return (data || []).map(({ book_genres, ...book }) => ({
        ...book,
        genres: ((book_genres || []) as { genre: GenreRow | GenreRow[] | null }[])
          .map((bg) => (Array.isArray(bg.genre) ? bg.genre[0] : bg.genre))
          .filter((g): g is GenreRow => !!g)
          .map(
            (g): LibraryGenre => ({
              id: g.id,
              name: getTranslatedGenreName(g, language),
              color: g.color,
            })
          ),
      }))
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  })
}

/** Sets end_date on several books at once (used to backfill read books without a date) */
export function useSetEndDates() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (updates: { bookId: string; endDate: string }[]) => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const results = await Promise.all(
        updates.map(({ bookId, endDate }) =>
          supabase
            .from('books')
            .update({ end_date: endDate })
            .eq('id', bookId)
            .eq('user_id', user.id)
        )
      )

      const failed = results.find((r) => r.error)
      if (failed?.error) {
        captureError(failed.error, { context: 'useSetEndDates', userId: user.id })
        throw failed.error
      }
    },
    onSuccess: () => invalidateLibraryQueries(queryClient, user?.id),
  })
}
